<script setup>
import { ref, watch, computed, nextTick, useSlots } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },

  size: {
    type: String,
    default: 'sm',
    validator: value => ['sm', 'lg'].includes(value),
  },
});

const emit = defineEmits(['mentionSelect', 'selectionChange']);

const slots = useSlots();
const { getPlainText } = useMessageFormatter();

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);

const hasPreview = computed(() => Boolean(slots.preview));
const selectedItem = computed(() => props.items[selectedIndex.value]);

const containerSizeClass = computed(() => {
  if (hasPreview.value) {
    return props.size === 'lg'
      ? 'w-[40rem] min-h-[12rem] max-h-[22rem]'
      : 'w-[32rem] min-h-[8rem] max-h-[9.75rem]';
  }

  return props.size === 'lg'
    ? 'w-[28rem] max-h-[22rem]'
    : 'w-full max-h-[9.75rem]';
});

const adjustScroll = () => {
  nextTick(() => {
    const container = mentionsListContainerRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#mention-item-${selectedIndex.value}`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  emit('mentionSelect', props.items[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => props.items),
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => props.items,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
  }
);

watch(selectedIndex, (index, previousIndex) => {
  adjustScroll();
  if (index === previousIndex) return;

  emit('selectionChange', props.items[index], index);
});

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div
    class="bg-n-solid-1 rounded-xl absolute z-20 shadow-md left-0 bottom-full border border-solid border-n-strong mention--box flex overflow-hidden"
    :class="containerSizeClass"
  >
    <div
      ref="mentionsListContainerRef"
      class="p-1 overflow-auto min-w-0"
      :class="hasPreview ? 'flex-1' : 'w-full'"
    >
      <ul class="mb-0 vertical dropdown menu">
        <woot-dropdown-item
          v-for="(item, index) in items"
          :id="`mention-item-${index}`"
          :key="item.key"
          class="!mb-1"
          @mouseover="onHover(index)"
        >
          <button
            class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
            :class="{
              'bg-n-alpha-black2': index === selectedIndex,
            }"
            @click="onListItemSelection(index)"
          >
            <slot
              :item="item"
              :index="index"
              :selected="index === selectedIndex"
            >
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ getPlainText(item.description) }}
              </p>
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ variableKey(item) }}
              </p>
            </slot>
          </button>

          <slot name="extra" :item="item" :index="index" />
        </woot-dropdown-item>
      </ul>
    </div>

    <div
      v-if="hasPreview && selectedItem"
      class="w-[17rem] shrink-0 border-l border-n-strong relative"
    >
      <div class="absolute inset-0 overflow-auto p-3">
        <slot name="preview" :item="selectedItem" :index="selectedIndex" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
